import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import FlowStrip from '../../help/FlowStrip';
import StatusStagesChip from '../../help/StatusStagesChip';
import { lcFlow } from '../../help/flows';
import { UD_STAGES } from '../../help/statusStages';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer,
    TableRow, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { UdRegisterService } from '../../services/udRegister.service';
import { PERMISSIONS } from '../../constants/permissions';
import { IUdRegister, UdStatus } from '../../interfaces/commercial.interface';
import { formatDate } from '../../utils/dateUtils';
import UdEntitlementLinePanel from './UdEntitlementLinePanel';
import { enumLabel } from '../../utils/garmentsLabels';

const STATUS_COLOR: Record<UdStatus, 'default' | 'info' | 'success' | 'warning'> = {
    DRAFT: 'default', SUBMITTED: 'info', APPROVED: 'success', CLOSED: 'warning',
};

/** §5.3's legal one-step transitions, rendered as a plain dropdown -- any illegal target is
 * refused server-side, this is just the UI's honest offering. */
const NEXT_STATUSES: Record<UdStatus, UdStatus[]> = {
    DRAFT: ['SUBMITTED'],
    SUBMITTED: ['DRAFT', 'APPROVED'],
    APPROVED: ['SUBMITTED', 'CLOSED'],
    CLOSED: [],
};

const UdRegisterDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.UD_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [ud, setUd] = useState<IUdRegister | null>(null);
    const [loading, setLoading] = useState(true);
    const [nextStatus, setNextStatus] = useState<UdStatus | ''>('');
    const [changingStatus, setChangingStatus] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const res = await UdRegisterService.get(institutionId, id!);
            setUd(res);
        } catch (e) {
            toast.showApiError(e, 'Failed to load UD register');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveWhy = changingStatus ? null
        : !nextStatus ? 'Pick the new status first.'
        : null;

    const handleStatusChange = async () => {
        if (!ud || !nextStatus) return;
        try {
            setChangingStatus(true);
            await UdRegisterService.update(institutionId, ud.id, {
                udNumber: ud.udNumber, udDate: ud.udDate, exportLcId: ud.exportLcId, bondLicenseId: ud.bondLicenseId,
                salesOrderId: ud.salesOrderId, salesOrderRef: ud.salesOrderRef,
                issuingAuthority: ud.issuingAuthority, approvalDate: ud.approvalDate,
                validTillDate: ud.validTillDate, lastAmendedDate: ud.lastAmendedDate,
                remarks: ud.remarks, status: nextStatus,
            });
            toast.showSuccess(`UD register moved to ${enumLabel(nextStatus)}`);
            setNextStatus('');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to change status');
        } finally {
            setChangingStatus(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
    if (!ud) return null;

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.udDetail.page">
            <FlowStrip data-help="commercial.ud.flow" stages={lcFlow({ current: 'ud', lc: { id: ud.exportLcId }, ud: { no: ud.udNumber } })} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{/^UD\b/i.test(ud.udNumber) ? ud.udNumber : `UD ${ud.udNumber}`}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {canManage && (
                        <Button data-help="commercial.udDetail.edit" onClick={() => navigate(`/commercial/ud-register/${ud.id}/edit`)}>Edit</Button>
                    )}
                </Box>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            <TableRow><TableCell>Status</TableCell>
                                <TableCell align="right"><StatusStagesChip data-help="commercial.ud.statusStages" label={enumLabel(ud.status)} status={ud.status} stages={UD_STAGES} color={STATUS_COLOR[ud.status]} /></TableCell></TableRow>
                            <TableRow><TableCell>UD Date</TableCell><TableCell align="right">{formatDate(ud.udDate)}</TableCell></TableRow>
                            <TableRow><TableCell>Issuing Authority</TableCell><TableCell align="right">{ud.issuingAuthority}</TableCell></TableRow>
                            <TableRow><TableCell>Valid Till</TableCell>
                                <TableCell align="right">{ud.validTillDate ? formatDate(ud.validTillDate) : '—'}</TableCell></TableRow>
                            <TableRow><TableCell>Due status</TableCell>
                                <TableCell align="right">{enumLabel(ud.dueStatus)}{ud.dueReason ? ` — ${ud.dueReason}` : ''}</TableCell></TableRow>
                            <TableRow><TableCell>Remarks</TableCell><TableCell align="right">{ud.remarks ?? '—'}</TableCell></TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {canManage && NEXT_STATUSES[ud.status].length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
                        <TextField data-help="commercial.udDetail.changeStatus" select size="small" label="Change status" value={nextStatus}
                            sx={{ minWidth: 200 }} onChange={e => setNextStatus(e.target.value as UdStatus)}>
                            {NEXT_STATUSES[ud.status].map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                        </TextField>
                        <Button data-help="commercial.udDetail.applyStatus" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="outlined" disabled={!nextStatus || changingStatus} onClick={handleStatusChange}>
                            Apply
                        </Button>
                    </Box>
                )}
            </Paper>

            <UdEntitlementLinePanel institutionId={institutionId} udId={ud.id} udStatus={ud.status} />
        </Box>
    );
};

export default UdRegisterDetail;
